import { IconButton, NavListItemText } from '@billy/design-system'
import { DragEvent, MouseEvent, ReactElement, ReactNode, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import * as Styled from './styles'

export interface TreeItemProps {
  children?: ReactNode
  depth?: number
  isExpanded?: boolean
  isSelected?: boolean
  label: string
  onClick: () => void
  onDragOver?: (event: DragEvent) => void
  onDrop?: (event: DragEvent) => void
  onToggle?: () => void
}

export const TreeItem = ({
  children,
  depth = 0,
  isExpanded = false,
  isSelected,
  label,
  onClick,
  onDragOver,
  onDrop,
  onToggle,
}: TreeItemProps): ReactElement => {
  const [isDragOver, setIsDragOver] = useState(false)
  const { t } = useTranslation()

  const handleToggle = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      onToggle?.()
    },
    [onToggle],
  )

  const handleDragOver = useCallback(
    (event: DragEvent) => {
      onDragOver?.(event)
      setIsDragOver(event.defaultPrevented)
    },
    [onDragOver],
  )

  const handleDragLeave = useCallback((event: DragEvent) => {
    // Ignore transitions into child elements (e.g. chevron, label) to prevent flicker
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return
    }
    setIsDragOver(false)
  }, [])

  const handleDragEnd = useCallback(() => {
    setIsDragOver(false)
  }, [])

  useEffect(() => {
    document.addEventListener('dragend', handleDragEnd)

    return () => {
      document.removeEventListener('dragend', handleDragEnd)
    }
  }, [handleDragEnd])

  const handleDrop = useCallback(
    (event: DragEvent) => {
      setIsDragOver(false)
      onDrop?.(event)
    },
    [onDrop],
  )

  return (
    <li>
      <Styled.Row
        $depth={depth}
        $isDragOver={isDragOver}
        $isSelected={isSelected}
        data-testid="tree-item-row"
        onClick={onClick}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {children ? (
          <IconButton
            icon={isExpanded ? 'chevronDown' : 'chevronRight'}
            onClick={handleToggle}
            aria-label={t('tree_item.toggle_label', { label })}
          />
        ) : (
          <Styled.IconPlaceholder />
        )}
        <NavListItemText>{label}</NavListItemText>
      </Styled.Row>
      {isExpanded && children}
    </li>
  )
}
